import { queryOptions } from '@tanstack/react-query';
import { getWorldcooGlobalStatsFn, getRecentDonationsFn } from './server-functions-portal';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * MARK: getWorldcooGlobalStats
 */
export const worldcooGlobalStatsOptions = () =>
	queryOptions({
		queryKey: ['worldcoo-global-stats'],
		queryFn: () => getWorldcooGlobalStatsFn(),
		// Stale time of 1 hour since stats don't change frequently
		staleTime: 60 * 60 * 1000,
	});

export function useWorldcooGlobalStatsQuery() {
	const { data } = useSuspenseQuery(worldcooGlobalStatsOptions());
	return data;
}

/**
 * MARK: getRecentDonations (for animated counter)
 *
 * Client passes the current 5-minute window key (e.g. "2026-02-13T14:10").
 * Server returns 6 minutes of donation data (1 min margin), sourced from
 * 1 day before. When the key changes to "2026-02-13T14:15", the old
 * data still covers ~1 min past the boundary while the new batch loads.
 */
export type LiveCounterMode = 'day' | 'realtime';

export const recentDonationsOptions = (windowKey: string, mode: LiveCounterMode = 'day') =>
	queryOptions({
		queryKey: ['worldcoo-recent-donations', mode, windowKey],
		queryFn: () => getRecentDonationsFn({ data: { windowKey, mode } }),
		// Cache for 10 minutes as this value is tied to a specific time window and won't change after that.
		staleTime: 10 * 60 * 1000,
	});

const WINDOW_MS = 5 * 60_000;

/** Returns the current 5-minute window key (e.g. "2026-02-13T14:10") */
export function currentWindowKey() {
	const windowStart = Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
	return new Date(windowStart).toISOString().slice(0, 16);
}
